/**
 * SignalsPanel — top 5 tags performants par revenue (S6 PR5).
 *
 * Délègue à `useDashboardTopTags` qui consomme `/api/signals/top-tags`
 * (déjà livré PR3-B). Si le refresh nightly n'a pas tourné (table vide),
 * `<EmptyState>` discret.
 *
 * Affiche : rank + slug + label + score + breakdown (revenue/sales/
 * favorites/views) au survol (title attribute pour MVP — tooltip riche
 * en S7+ si besoin).
 */

import EmptyState from '@/components/ui/EmptyState'
import { useDashboardTopTags, type TopTagEntry } from '@/hooks/useDashboard'

import './SignalsPanel.css'

const DEFAULT_LIMIT = 5

export default function SignalsPanel() {
  const query = useDashboardTopTags(DEFAULT_LIMIT)

  if (query.isLoading) {
    return (
      <section className="signals-panel" aria-busy="true">
        <h2 className="ui-h4 signals-panel-title">Signaux Etsy</h2>
        <p className="ui-body ui-text-muted">Chargement…</p>
      </section>
    )
  }

  if (query.isError) {
    return (
      <section className="signals-panel">
        <h2 className="ui-h4 signals-panel-title">Signaux Etsy</h2>
        <p className="ui-body ui-text-muted">Indisponibles.</p>
      </section>
    )
  }

  const tags = query.data ?? []

  return (
    <section className="signals-panel" aria-label="Signaux d'apprentissage Etsy">
      <h2 className="ui-h4 signals-panel-title">Top tags Etsy par revenu</h2>
      {tags.length === 0 ? (
        <EmptyState
          title="Aucun signal"
          description={
            'Le calcul nightly (04:30 UTC) tourne après les premières ventes. ' +
            'Reviens demain ou approuve un draft pour générer du signal.'
          }
        />
      ) : (
        <ol className="signals-panel-list">
          {tags.map((tag) => (
            <SignalRow key={tag.tag_id} tag={tag} />
          ))}
        </ol>
      )}
    </section>
  )
}

interface SignalRowProps {
  tag: TopTagEntry
}

function SignalRow({ tag }: SignalRowProps) {
  const breakdown = tag.score_breakdown
  const tooltip = Object.entries(breakdown)
    .map(([k, v]) => `${k}: ${(v * 100).toFixed(1)}%`)
    .join(' · ')

  return (
    <li className="signals-panel-row" title={tooltip}>
      <span className="signals-panel-rank ui-body-sm ui-text-muted">#{tag.rank}</span>
      <span className="signals-panel-label ui-body">{tag.tag_label_fr ?? tag.tag_slug}</span>
      <span className="signals-panel-score ui-body-sm">{(tag.score * 100).toFixed(0)}</span>
    </li>
  )
}
